import Phaser from "phaser"
import { BOARD_HEIGHT, BOARD_WIDTH, SynergyTriggers, getRegionTint } from "../../../../config"
import { DesignTiled } from "../../../../core/design"
import {
  FLOWER_POTS_POSITIONS_BLUE,
  FLOWER_POTS_POSITIONS_RED,
  FlowerPots
} from "../../../../core/flower-pots"
import Simulation from "../../../../core/simulation"
import Player from "../../../../models/colyseus-models/player"
import PokemonFactory from "../../../../models/pokemon-factory"
import { DungeonPMDO } from "../../../../types/enum/Dungeon"
import { PokemonActionState, PokemonTint } from "../../../../types/enum/Game"
import { Pkm } from "../../../../types/enum/Pokemon"
import { Synergy } from "../../../../types/enum/Synergy"
import { Weather } from "../../../../types/enum/Weather"
import {
  SimulatorBackgroundTheme,
  SimulatorBattlePreview,
  SimulatorPreparedFlowerPotPreview,
  SimulatorSelectedPokemonDetail
} from "../../../../types/interfaces/simulator"
import { logger } from "../../../../utils/logger"
import { transformEntityCoordinates } from "../../pages/utils/utils"
import AnimationManager from "../animation-manager"
import BattleManager from "../components/battle-manager"
import PokemonSprite, { loadCompressedAtlas } from "../components/pokemon"
import LoadingManager, {
  preloadDefaultPortraits
} from "../components/loading-manager"
import { preference } from "../../preferences"
import WeatherManager from "../components/weather-manager"
import { DEPTH } from "../depths"

export default class SimulatorScene extends Phaser.Scene {
  private static readonly BATTLEFIELD_BOUNDS =
    SimulatorScene.computeBattlefieldBounds()
  private static readonly BOARD_CENTER_X =
    (SimulatorScene.BATTLEFIELD_BOUNDS.minX +
      SimulatorScene.BATTLEFIELD_BOUNDS.maxX) /
    2
  private static readonly BOARD_CENTER_Y =
    (SimulatorScene.BATTLEFIELD_BOUNDS.minY +
      SimulatorScene.BATTLEFIELD_BOUNDS.maxY) /
    2
  private static readonly BOARD_FRAME_WIDTH = 920
  private static readonly BOARD_FRAME_HEIGHT = 620
  private static readonly BOARD_CAMERA_PADDING_X = 24
  private static readonly BOARD_CAMERA_PADDING_Y = 16
  private static readonly BACKDROP_COLOR = 0x060d15
  private static readonly BOARD_CELL_TINT = 0xffffff
  private static readonly MAP_DEPTH = -30
  private static readonly MAP_SCALE = 2
  private static readonly MAP_VIEW_OFFSET_X = 210
  private static readonly MAP_VIEW_OFFSET_Y = 308
  private static readonly MAP_VIEW_TILE_SHIFT_X = -1
  private static readonly FLOWER_POT_OFFSET_X = 64
  private static readonly FLOWER_POT_RED_OFFSET_X = -64
  private static readonly FLOWER_POT_BATTLE_OFFSET_Y = 18

  uid = "simulator-blue"
  mapName: SimulatorBackgroundTheme = "town"
  spectate = true
  inspectOnLeftClick = true
  renderDetailInPanel = false
  battleGroup?: Phaser.GameObjects.Group
  previewGroup?: Phaser.GameObjects.Group
  backdropRect?: Phaser.GameObjects.Rectangle
  boardCells: Phaser.GameObjects.Image[] = []
  animationManager?: AnimationManager
  weatherManager?: WeatherManager
  battle?: BattleManager
  spotlightMarkers: Phaser.GameObjects.Rectangle[] = []
  groundHoleSprites: Phaser.GameObjects.Sprite[] = []
  flowerPotSprites: Phaser.GameObjects.Sprite[] = []
  flowerPokemonsInPots: PokemonSprite[] = []
  map?: Phaser.Tilemaps.Tilemap
  backgroundLayers: Array<Phaser.Tilemaps.TilemapLayer | Phaser.Tilemaps.TilemapGPULayer> = []
  tilemaps = new Map<DungeonPMDO, DesignTiled>()
  backgroundRequestId = 0
  pokemonAssetRequestId = 0
  selectedPokemonVersion = 0
  pokemonHovered: PokemonSprite | null = null
  lastPokemonDetail: PokemonSprite | null = null
  board = {
    pokemons: new Map(),
    flowerPokemonsInPots: [] as PokemonSprite[],
    closeTooltips: () => {}
  } as any
  pendingPlayer?: Player
  pendingBluePlayer?: Player
  pendingRedPlayer?: Player
  pendingSimulation?: Simulation
  pendingPreview?: SimulatorBattlePreview
  pendingBackgroundTheme: SimulatorBackgroundTheme = "town"
  onSelectedPokemonChange?: (
    detail: SimulatorSelectedPokemonDetail | null
  ) => void

  constructor() {
    super({ key: "simulatorScene" })
  }

  private static computeBattlefieldBounds() {
    let minX = Number.POSITIVE_INFINITY
    let maxX = Number.NEGATIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    let maxY = Number.NEGATIVE_INFINITY

    for (let y = 0; y < BOARD_HEIGHT; y++) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const [cellX, cellY] = transformEntityCoordinates(x, y, false)
        const visualY = cellY + 18
        minX = Math.min(minX, cellX)
        maxX = Math.max(maxX, cellX)
        minY = Math.min(minY, visualY)
        maxY = Math.max(maxY, visualY)
      }
    }

    return { minX, maxX, minY, maxY }
  }

  preload() {
    new LoadingManager(this)
    preloadDefaultPortraits(this)
  }

  create() {
    this.fitBattlefieldToViewport()
    this.createBackdrop()
    this.registerPointerInteractions()
    void this.applyBackgroundTheme(this.pendingBackgroundTheme)

    this.previewGroup = this.add.group()
    this.battleGroup = this.add.group()
    this.animationManager = new AnimationManager(this)
    this.weatherManager = new WeatherManager(this)
    this.battle = new BattleManager(
      this as any,
      this.battleGroup,
      this.pendingSimulation,
      this.animationManager,
      (this.pendingPlayer ?? { id: "simulator-blue" }) as Player
    )

    if (this.pendingPlayer) {
      this.battle.setPlayer(this.pendingPlayer)
    }

    if (this.pendingSimulation) {
      void this.renderSimulationState(this.pendingSimulation)
    } else if (this.pendingPreview) {
      void this.renderPreviewState(this.pendingPreview)
    }
  }

  update() {
    if (this.lastPokemonDetail) {
      this.lastPokemonDetail.updateTooltipPosition()
    }
  }

  setPlayer(player: Player) {
    this.pendingPlayer = player
    this.uid = player.id
    this.battle?.setPlayer(player)
  }

  setSimulationPlayers(bluePlayer?: Player, redPlayer?: Player) {
    this.pendingBluePlayer = bluePlayer
    this.pendingRedPlayer = redPlayer
  }

  setPreview(preview: SimulatorBattlePreview) {
    this.pendingPreview = preview
    if (!this.pendingSimulation) {
      void this.renderPreviewState(preview)
    }
  }

  setSelectedPokemonListener(
    listener?: (detail: SimulatorSelectedPokemonDetail | null) => void
  ) {
    this.onSelectedPokemonChange = listener
    this.emitSelectedPokemonDetail()
  }

  setBackgroundTheme(theme: SimulatorBackgroundTheme) {
    this.pendingBackgroundTheme = theme
    if (!this.sys.isActive()) {
      return
    }
    void this.applyBackgroundTheme(theme)
  }

  setSimulation(simulation: Simulation) {
    this.pendingSimulation = simulation
    this.clearPreview()
    void this.renderSimulationState(simulation)
  }

  refreshSimulationDecorations() {
    if (!this.pendingSimulation) {
      return
    }

    this.updateSpotlightMarkers(this.pendingSimulation)
    this.updateGroundHoleMarkers(this.pendingSimulation)
    this.updateFlowerPotMarkers(this.pendingSimulation)
    this.fitBattlefieldToViewport()
  }

  refreshVisualState() {
    if (this.pendingSimulation) {
      this.refreshSimulationDecorations()
      return
    }

    if (this.pendingPreview) {
      void this.renderPreviewState(this.pendingPreview)
    }
  }

  clearSimulation() {
    this.pendingSimulation = undefined
    this.weatherManager?.clearWeather()
    this.clearSpotlightMarkers()
    this.clearGroundHoleMarkers()
    this.clearFlowerPotMarkers()
    this.battle?.clear()
    if (this.pendingPreview) {
      this.renderPreview(this.pendingPreview)
    }
  }

  selectPokemonDetail(pokemonSprite: PokemonSprite | null) {
    this.lastPokemonDetail = pokemonSprite
    this.emitSelectedPokemonDetail()
  }

  refreshSelectedPokemonDetail() {
    this.emitSelectedPokemonDetail()
  }

  private getDropTargetSpriteAt(worldX: number, worldY: number) {
    const previewSprites =
      this.previewGroup?.getChildren().filter((child): child is PokemonSprite => {
        return child instanceof PokemonSprite
      }) ?? []
    const battleSprites = Array.from(this.battle?.pokemonSprites.values() ?? [])
    const sprites = [
      ...battleSprites,
      ...previewSprites,
      ...this.flowerPokemonsInPots
    ]

    for (let index = sprites.length - 1; index >= 0; index -= 1) {
      const sprite = sprites[index]
      const bounds = sprite.sprite?.getBounds() ?? sprite.getBounds()
      const hitbox = new Phaser.Geom.Rectangle(
        bounds.x - 18,
        bounds.y - 18,
        bounds.width + 36,
        bounds.height + 36
      )

      if (Phaser.Geom.Rectangle.Contains(hitbox, worldX, worldY)) {
        return sprite
      }
    }

    return null
  }

  getDropTargetAt(worldX: number, worldY: number) {
    const sprite = this.getDropTargetSpriteAt(worldX, worldY)
    if (!sprite) {
      return null
    }

    return {
      kind: sprite.dropTargetKind,
      x: sprite.dropTargetX ?? sprite.pokemon.positionX,
      y: sprite.dropTargetY ?? sprite.pokemon.positionY,
      index: sprite.dropTargetIndex,
      isAlly: sprite.playerId === this.uid
    }
  }

  setDropHoverAt(worldX: number, worldY: number) {
    const sprite = this.getDropTargetSpriteAt(worldX, worldY)
    if (sprite) {
      this.setPokemonHovered(sprite)
      return
    }

    if (this.pokemonHovered) {
      this.clearHovered(this.pokemonHovered.sprite)
      this.pokemonHovered = null
    }
  }

  displayBattleText(
    x: number,
    y: number,
    label: string,
    tweenOut: boolean = false
  ) {
    const textStyle = {
      fontSize: "24px",
      fontFamily: "Jost",
      color: "#fff",
      align: "center",
      strokeThickness: 2,
      stroke: "#000"
    }

    const text = this.add
      .text(x, y, label, textStyle)
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH.TEXT)

    if (tweenOut) {
      this.add.tween({
        targets: [text],
        ease: "linear",
        duration: 1500,
        alpha: {
          getStart: () => 1,
          getEnd: () => 0
        },
        y: {
          getStart: () => y - 50,
          getEnd: () => y - 110
        },
        onComplete: () => {
          text.destroy()
        }
      })
    }

    return text
  }

  shakeCamera(options?: { intensity?: number; duration?: number }) {
    if (preference("disableCameraShake")) {
      return
    }
    this.cameras.main.shake(
      options?.duration ?? 250,
      options?.intensity ?? 0.01
    )
  }

  private createBackdrop() {
    this.cameras.main.setBackgroundColor("#040a11")

    this.backdropRect = this.add
      .rectangle(
        SimulatorScene.BOARD_CENTER_X,
        SimulatorScene.BOARD_CENTER_Y,
        1950,
        1000,
        SimulatorScene.BACKDROP_COLOR,
        1
      )
      .setDepth(SimulatorScene.MAP_DEPTH - 2)

    for (let y = 0; y < BOARD_HEIGHT; y++) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const [cellX, cellY] = transformEntityCoordinates(x, y, false)
        const cell = this.add
          .image(cellX, cellY + 18, "board_cell", 0)
          .setScale(2, 2)
          .setTint(SimulatorScene.BOARD_CELL_TINT)
          .setAlpha((x + y) % 2 === 0 ? 0.18 : 0.1)
          .setDepth(-8)
        this.boardCells.push(cell)
      }
    }
  }

  private applyWeather(weather: Weather) {
    if (!this.weatherManager) return
    this.weatherManager.clearWeather()

    switch (weather) {
      case Weather.RAIN:
        this.weatherManager.addRain()
        break
      case Weather.ZENITH:
        this.weatherManager.addSun()
        break
      case Weather.DROUGHT:
        this.weatherManager.addDrought()
        break
      case Weather.SANDSTORM:
        this.weatherManager.addSandstorm()
        break
      case Weather.SNOW:
        this.weatherManager.addSnow()
        break
      case Weather.NIGHT:
        this.weatherManager.addNight()
        break
      case Weather.BLOODMOON:
        this.weatherManager.addBloodMoon()
        break
      case Weather.WINDY:
        this.weatherManager.addWind()
        break
      case Weather.STORM:
        this.weatherManager.addStorm()
        break
      case Weather.MISTY:
        this.weatherManager.addMist()
        break
      case Weather.SMOG:
        this.weatherManager.addSmog()
        break
      case Weather.MURKY:
        this.weatherManager.addMurky()
        break
      default:
        break
    }
  }

  private fitBattlefieldToViewport() {
    const camera = this.cameras.main
    let minX =
      SimulatorScene.BOARD_CENTER_X - SimulatorScene.BOARD_FRAME_WIDTH / 2
    let maxX =
      SimulatorScene.BOARD_CENTER_X + SimulatorScene.BOARD_FRAME_WIDTH / 2
    let minY =
      SimulatorScene.BOARD_CENTER_Y - SimulatorScene.BOARD_FRAME_HEIGHT / 2
    let maxY =
      SimulatorScene.BOARD_CENTER_Y + SimulatorScene.BOARD_FRAME_HEIGHT / 2

    this.flowerPotSprites.forEach((sprite) => {
      const bounds = sprite.getBounds()
      minX = Math.min(minX, bounds.left)
      maxX = Math.max(maxX, bounds.right)
      minY = Math.min(minY, bounds.top)
      maxY = Math.max(maxY, bounds.bottom)
    })

    const zoom = Math.min(
      camera.width / (maxX - minX + SimulatorScene.BOARD_CAMERA_PADDING_X * 2),
      camera.height /
        (maxY - minY + SimulatorScene.BOARD_CAMERA_PADDING_Y * 2)
    )

    camera.setZoom(Math.max(1, zoom))
    camera.centerOn((minX + maxX) / 2, (minY + maxY) / 2)
  }

  private async applyBackgroundTheme(theme: SimulatorBackgroundTheme) {
    const requestId = ++this.backgroundRequestId
    this.pendingBackgroundTheme = theme
    this.mapName = theme
    this.styleBoardOverlay()

    try {
      const mapKey = await this.ensureBackgroundAssets(theme)
      if (
        requestId !== this.backgroundRequestId ||
        this.pendingBackgroundTheme !== theme
      ) {
        return
      }

      this.renderBackgroundMap(theme, mapKey)
    } catch (error) {
      logger.error(`failed to load simulator background ${theme}`, error)
      if (
        requestId !== this.backgroundRequestId ||
        this.pendingBackgroundTheme !== theme
      ) {
        return
      }
      this.clearBackgroundMap()
    }
  }

  private styleBoardOverlay() {
    this.cameras.main.setBackgroundColor("#040a11")
    this.backdropRect?.setFillStyle(SimulatorScene.BACKDROP_COLOR, 1)

    this.boardCells.forEach((cell, index) => {
      const x = index % BOARD_WIDTH
      const y = Math.floor(index / BOARD_WIDTH)
      cell
        .setTint(SimulatorScene.BOARD_CELL_TINT)
        .setAlpha((x + y) % 2 === 0 ? 0.18 : 0.1)
    })
  }

  private async ensureBackgroundAssets(theme: SimulatorBackgroundTheme) {
    if (theme === "town") {
      return "town"
    }

    let tilemap = this.tilemaps.get(theme)
    if (!tilemap) {
      const response = await fetch(`/tilemap/${theme}`)
      if (!response.ok) {
        throw new Error(`Failed to fetch tilemap ${theme}: ${response.status}`)
      }
      tilemap = (await response.json()) as DesignTiled
      this.tilemaps.set(theme, tilemap)
    }

    const mapKey = `simulator-map-${theme}`
    const missingTilesets = tilemap.tilesets.filter(
      (tileset) => !this.textures.exists(`${theme}/${tileset.name}`)
    )

    if (this.cache.tilemap.exists(mapKey) && missingTilesets.length === 0) {
      return mapKey
    }

    await new Promise<void>((resolve) => {
      let queuedFiles = false

      missingTilesets.forEach((tileset) => {
        queuedFiles = true
        this.load.image(
          `${theme}/${tileset.name}`,
          `/assets/tilesets/${theme}/${tileset.image}`
        )
      })

      if (!this.cache.tilemap.exists(mapKey)) {
        queuedFiles = true
        this.load.tilemapTiledJSON(mapKey, tilemap)
      }

      if (!queuedFiles) {
        resolve()
        return
      }

      this.load.once("complete", () => resolve())
      if (!this.load.isLoading()) {
        this.load.start()
      }
    })

    return mapKey
  }

  private renderBackgroundMap(
    theme: SimulatorBackgroundTheme,
    mapKey: SimulatorBackgroundTheme | string
  ) {
    this.clearBackgroundMap()

    const map =
      theme === "town"
        ? this.add.tilemap("town")
        : this.make.tilemap({ key: mapKey })
    const [offsetX, offsetY] = this.getBackgroundMapOffset(map)

    if (theme === "town") {
      const tileset = map.addTilesetImage("town_tileset", "town_tileset")
      if (tileset) {
        ;["layer0", "layer1", "layer2"].forEach((layerName, index) => {
          const layer = map
            .createLayer(layerName, tileset, offsetX, offsetY)
            ?.setScale(SimulatorScene.MAP_SCALE, SimulatorScene.MAP_SCALE)
            .setDepth(SimulatorScene.MAP_DEPTH + index)
          if (layer) {
            this.backgroundLayers.push(layer)
          }
        })
      }
      const sys = this.sys as any
      if (sys.animatedTiles) {
        sys.animatedTiles.pause()
      }
    } else {
      const tilemap = this.tilemaps.get(theme)
      tilemap?.layers.forEach((layerConfig, index) => {
        const tileset = map.addTilesetImage(
          layerConfig.name,
          `${theme}/${layerConfig.name}`
        )
        const layer = map
          .createLayer(layerConfig.name, tileset!, offsetX, offsetY)
          ?.setScale(SimulatorScene.MAP_SCALE, SimulatorScene.MAP_SCALE)
          .setDepth(SimulatorScene.MAP_DEPTH + index)
        if (layer) {
          this.backgroundLayers.push(layer)
        }
      })
      ;(this.sys as any).animatedTiles?.init(map)
    }

    this.map = map
  }

  private getBackgroundMapOffset(map: Phaser.Tilemaps.Tilemap) {
    const scaledWidth = map.widthInPixels * SimulatorScene.MAP_SCALE
    const scaledHeight = map.heightInPixels * SimulatorScene.MAP_SCALE
    const tileShiftX =
      map.tileWidth *
      SimulatorScene.MAP_SCALE *
      SimulatorScene.MAP_VIEW_TILE_SHIFT_X
    const centeredOffsetX = SimulatorScene.BOARD_CENTER_X - scaledWidth / 2
    const centeredOffsetY = SimulatorScene.BOARD_CENTER_Y - scaledHeight / 2
    const minOffsetX =
      SimulatorScene.BOARD_CENTER_X + SimulatorScene.BOARD_FRAME_WIDTH / 2 - scaledWidth
    const maxOffsetX =
      SimulatorScene.BOARD_CENTER_X - SimulatorScene.BOARD_FRAME_WIDTH / 2
    const minOffsetY =
      SimulatorScene.BOARD_CENTER_Y + SimulatorScene.BOARD_FRAME_HEIGHT / 2 - scaledHeight
    const maxOffsetY =
      SimulatorScene.BOARD_CENTER_Y - SimulatorScene.BOARD_FRAME_HEIGHT / 2

    return [
      Phaser.Math.Clamp(
        centeredOffsetX + SimulatorScene.MAP_VIEW_OFFSET_X + tileShiftX,
        minOffsetX,
        maxOffsetX
      ),
      Phaser.Math.Clamp(
        centeredOffsetY + SimulatorScene.MAP_VIEW_OFFSET_Y,
        minOffsetY,
        maxOffsetY
      )
    ] as const
  }

  private clearBackgroundMap() {
    this.backgroundLayers.forEach((layer) => layer.destroy())
    this.backgroundLayers = []
    this.map?.destroy()
    this.map = undefined
  }

  private updateSpotlightMarkers(simulation: Simulation) {
    this.clearSpotlightMarkers()
    const bluePlayer = this.pendingBluePlayer
    const redPlayer = this.pendingRedPlayer
    const lightTrigger = SynergyTriggers[Synergy.LIGHT][0] ?? 0

    const markers = [
      {
        x: bluePlayer?.lightX,
        y: bluePlayer?.lightY,
        active: (bluePlayer?.synergies.get(Synergy.LIGHT) ?? 0) >= lightTrigger
      },
      {
        x: redPlayer?.lightX,
        y: redPlayer?.lightY,
        active: (redPlayer?.synergies.get(Synergy.LIGHT) ?? 0) >= lightTrigger
      }
    ]

    markers.forEach((marker, index) => {
      if (!marker.active || marker.x == null || marker.y == null) {
        return
      }

      const boardY = index === 0 ? marker.y - 1 : BOARD_HEIGHT - marker.y
      const [cellX, cellY] = transformEntityCoordinates(marker.x, boardY, false)
      const rect = this.add
        .rectangle(cellX, cellY + 18, 156, 156, 0xf6df72, 0.1)
        .setStrokeStyle(4, 0xfbe89f, 0.8)
        .setDepth(-7.5)

      this.spotlightMarkers.push(rect)
    })
  }

  private clearSpotlightMarkers() {
    this.spotlightMarkers.forEach((marker) => marker.destroy())
    this.spotlightMarkers = []
  }

  private updateGroundHoleMarkers(simulation: Simulation) {
    this.clearGroundHoleMarkers()
    this.renderPlayerGroundHoles(this.pendingBluePlayer, false)
    this.renderPlayerGroundHoles(this.pendingRedPlayer, true)
  }

  private updateFlowerPotMarkers(simulation: Simulation) {
    this.clearFlowerPotMarkers()
    this.renderPlayerFlowerPots(simulation, this.pendingBluePlayer, false)
    this.renderPlayerFlowerPots(simulation, this.pendingRedPlayer, true)
  }

  private renderPlayerGroundHoles(player: Player | undefined, isRedTeam: boolean) {
    if (!player) {
      return
    }

    const groundTrigger = SynergyTriggers[Synergy.GROUND][0] ?? 0
    if ((player.synergies.get(Synergy.GROUND) ?? 0) < groundTrigger) {
      return
    }

    const holes: Array<{ x: number; y: number; level: number }> = []
    for (let row = 0; row < BOARD_HEIGHT / 2; row++) {
      for (let col = 0; col < BOARD_WIDTH; col++) {
        const level = player.groundHoles[col + row * BOARD_WIDTH] ?? 0
        if (level <= 0) {
          continue
        }

        holes.push({ x: col, y: row + 1, level })
      }
    }

    this.renderGroundHoleLayout(holes, isRedTeam)
  }

  private renderPreviewGroundHoles(
    holes: Array<{ x: number; y: number; level: number }>,
    isRedTeam: boolean
  ) {
    this.renderGroundHoleLayout(holes, isRedTeam)
  }

  private renderGroundHoleLayout(
    holes: Array<{ x: number; y: number; level: number }>,
    isRedTeam: boolean
  ) {
    const rendered = new Set<string>()
    const holeMap = new Map(
      holes.map((hole) => [`${hole.x},${hole.y}`, hole] as const)
    )

    holes.forEach((hole) => {
      const key = `${hole.x},${hole.y}`
      if (rendered.has(key)) {
        return
      }

      const horizontalRun = this.getHorizontalGroundHoleRun(hole, holeMap)
      if (hole.level >= 5 && horizontalRun.length >= 2) {
        horizontalRun.forEach((cell) => rendered.add(`${cell.x},${cell.y}`))
        this.renderGroundTrench(horizontalRun, isRedTeam)
        return
      }

      rendered.add(key)
      this.renderGroundHoleSprite(hole.x, hole.y, hole.level, isRedTeam)
    })
  }

  private getHorizontalGroundHoleRun(
    hole: { x: number; y: number; level: number },
    holeMap: Map<string, { x: number; y: number; level: number }>
  ) {
    const run = [hole]

    for (let x = hole.x - 1; x >= 0; x--) {
      const adjacent = holeMap.get(`${x},${hole.y}`)
      if (!adjacent || adjacent.level < 5) {
        break
      }
      run.unshift(adjacent)
    }

    for (let x = hole.x + 1; x < BOARD_WIDTH; x++) {
      const adjacent = holeMap.get(`${x},${hole.y}`)
      if (!adjacent || adjacent.level < 5) {
        break
      }
      run.push(adjacent)
    }

    return run
  }

  private renderGroundTrench(
    holes: Array<{ x: number; y: number; level: number }>,
    isRedTeam: boolean
  ) {
    const first = holes[0]
    const last = holes[holes.length - 1]
    const boardY = isRedTeam ? BOARD_HEIGHT - first.y : first.y - 1
    const [startX, cellY] = transformEntityCoordinates(first.x, boardY, false)
    const [endX] = transformEntityCoordinates(last.x, boardY, false)
    const trench = this.add
      .sprite(
        (startX + endX) / 2,
        cellY + 10,
        "ground_holes",
        `trench${holes.length}.png`
      )
      .setScale(2)
      .setDepth(DEPTH.BOARD_EFFECT_GROUND_LEVEL)
      .setTint(getRegionTint(this.mapName, preference("colorblindMode")))

    this.groundHoleSprites.push(trench)
  }

  private renderGroundHoleSprite(
    x: number,
    y: number,
    level: number,
    isRedTeam: boolean
  ) {
    const boardY = isRedTeam ? BOARD_HEIGHT - y : y - 1
    const [cellX, cellY] = transformEntityCoordinates(x, boardY, false)
    const groundHole = this.add
      .sprite(cellX, cellY + 10, "ground_holes", `hole${Math.min(5, level)}.png`)
      .setScale(2)
      .setDepth(DEPTH.BOARD_EFFECT_GROUND_LEVEL)
      .setTint(getRegionTint(this.mapName, preference("colorblindMode")))

    this.groundHoleSprites.push(groundHole)
  }

  private clearGroundHoleMarkers() {
    this.groundHoleSprites.forEach((sprite) => sprite.destroy())
    this.groundHoleSprites = []
  }

  private renderPlayerFlowerPots(
    simulation: Simulation,
    player: Player | undefined,
    isRedTeam: boolean
  ) {
    if (!player) {
      return
    }

    const floraLevel = player.synergies.get(Synergy.FLORA) ?? 0
    let nbPots = SynergyTriggers[Synergy.FLORA].filter(
      (threshold) => threshold <= floraLevel
    ).length
    if (
      floraLevel >= 6 &&
      player.flowerPots.every((pokemon) => pokemon.evolution === Pkm.DEFAULT)
    ) {
      nbPots = 5
    }

    for (let index = 0; index < nbPots; index++) {
      const potPokemon = player.flowerPots[index]

      this.renderFlowerPot(
        potPokemon,
        index,
        player.id,
        isRedTeam,
        false
      )
    }
  }

  private renderPreviewFlowerPots(
    flowerPots: SimulatorPreparedFlowerPotPreview[],
    teamId: string,
    isRedTeam: boolean
  ) {
    flowerPots.forEach((flowerPot, index) => {
      const pokemon = PokemonFactory.createPokemonFromName(flowerPot.pokemon)
      if (flowerPot.maxHPBonus > 0) {
        pokemon.addMaxHP(flowerPot.maxHPBonus)
      }
      if (flowerPot.apBonus > 0) {
        pokemon.addAbilityPower(flowerPot.apBonus)
      }
      pokemon.action = PokemonActionState.SLEEP
      this.renderFlowerPot(pokemon, index, teamId, isRedTeam)
    })
  }

  private renderFlowerPot(
    potPokemon:
      | Player["flowerPots"][number]
      | ReturnType<typeof PokemonFactory.createPokemonFromName>
      | undefined,
    index: number,
    teamId: string,
    isRedTeam: boolean,
    showFlowerSprite: boolean = true
  ) {
    if (!potPokemon) {
      return
    }

    const positions = isRedTeam
      ? FLOWER_POTS_POSITIONS_RED
      : FLOWER_POTS_POSITIONS_BLUE
    const [baseX, y] = positions[index] ?? []
    const x =
      baseX +
      SimulatorScene.FLOWER_POT_OFFSET_X +
      (isRedTeam ? SimulatorScene.FLOWER_POT_RED_OFFSET_X : 0)
    if (x == null || y == null) {
      return
    }
    const potY = showFlowerSprite
      ? y
      : y + SimulatorScene.FLOWER_POT_BATTLE_OFFSET_Y

    const potSprite = this.add
      .sprite(x, potY, "flower_pots", `${FlowerPots[index]}.png`)
      .setDepth(index % 2 ? DEPTH.INANIMATE_OBJECTS : DEPTH.INANIMATE_OBJECTS + 0.1)
      .setScale(2, 2)
      .setOrigin(0.5, 0.5)
      .setTint(getRegionTint(this.mapName, preference("colorblindMode")))

    this.flowerPotSprites.push(potSprite)

    if (!showFlowerSprite) {
      return
    }

    const flowerInPot = new PokemonSprite(
      this as any,
      x,
      y - 24,
      potPokemon,
      teamId,
      false,
      isRedTeam
    )
    flowerInPot.dropTargetKind = "flowerPot"
    flowerInPot.dropTargetIndex = index
    this.animationManager?.animatePokemon(
      flowerInPot,
      PokemonActionState.SLEEP,
      isRedTeam,
      true
    )
    flowerInPot.draggable = false
    this.flowerPokemonsInPots.push(flowerInPot)
    this.board.pokemons.set(flowerInPot.id, flowerInPot)
    this.board.flowerPokemonsInPots = this.flowerPokemonsInPots
  }

  private clearFlowerPotMarkers() {
    this.flowerPotSprites.forEach((sprite) => sprite.destroy())
    this.flowerPokemonsInPots.forEach((pokemon) => {
      pokemon.destroy()
      this.board.pokemons.delete(pokemon.id)
    })
    this.flowerPotSprites = []
    this.flowerPokemonsInPots = []
    this.board.flowerPokemonsInPots = this.flowerPokemonsInPots
  }

  private registerPointerInteractions() {
    this.input.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
      if (!pointer.rightButtonDown()) {
        this.closeTooltips()
      }
    })

    this.input.on(
      Phaser.Input.Events.GAMEOBJECT_OVER,
      (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject) => {
        if (gameObject instanceof PokemonSprite) {
          this.setPokemonHovered(gameObject)
        }
      }
    )

    this.input.on(
      Phaser.Input.Events.GAMEOBJECT_OUT,
      (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject) => {
        if (this.pokemonHovered === gameObject) {
          this.clearHovered(this.pokemonHovered.sprite)
          this.pokemonHovered = null
        }
      }
    )
  }

  closeTooltips() {
    this.battle?.closeTooltips()
    this.previewGroup?.getChildren().forEach((child) => {
      if (child instanceof PokemonSprite) {
        child.closeDetail()
      }
    })
    this.flowerPokemonsInPots.forEach((pokemon) => pokemon.closeDetail())
  }

  resetOverlayState() {
    this.closeTooltips()
    if (this.pokemonHovered) {
      this.clearHovered(this.pokemonHovered.sprite)
      this.pokemonHovered = null
    }
    this.lastPokemonDetail = null
    this.emitSelectedPokemonDetail()
  }

  clearDropHover() {
    if (this.pokemonHovered) {
      this.clearHovered(this.pokemonHovered.sprite)
      this.pokemonHovered = null
    }
  }

  setPokemonHovered(pokemonSprite: PokemonSprite) {
    if (this.pokemonHovered === pokemonSprite) {
      return
    }
    if (this.pokemonHovered != null && this.pokemonHovered !== pokemonSprite) {
      this.clearHovered(this.pokemonHovered.sprite)
    }
    this.pokemonHovered = pokemonSprite
    this.setHovered(pokemonSprite.sprite, 3)
  }

  setHovered(sprite: Phaser.GameObjects.Sprite, thickness = 2) {
    if (!sprite?.active) return
    sprite.setTint(0xffffcc)
    sprite.setData("simulatorHoverTint", true)
  }

  clearHovered(sprite?: Phaser.GameObjects.Sprite) {
    if (!sprite?.active || !sprite.getData("simulatorHoverTint")) return
    sprite.clearTint()
    sprite.setData("simulatorHoverTint", false)
  }

  private renderPreview(preview: SimulatorBattlePreview) {
    this.clearPreview()
    this.clearGroundHoleMarkers()
    this.clearFlowerPotMarkers()
    this.renderPreviewGroundHoles(preview.blueGroundHoles, false)
    this.renderPreviewGroundHoles(preview.redGroundHoles, true)
    this.renderPreviewFlowerPots(preview.blueFlowerPots, this.uid, false)
    this.renderPreviewFlowerPots(preview.redFlowerPots, "simulator-red", true)

    const renderTeam = (
      teamPreview: SimulatorBattlePreview["blue"],
      teamId: string,
      isRedTeam: boolean
    ) => {
      teamPreview.forEach((unit) => {
        const pokemon = PokemonFactory.createPokemonFromName(unit.name, {
          emotion: unit.emotion,
          shiny: unit.shiny
        })
        pokemon.id = unit.id
        pokemon.positionX = unit.x
        pokemon.positionY = unit.y
        unit.items.forEach((item) => pokemon.items.add(item))
        if (unit.tm) {
          pokemon.tm = unit.tm
          pokemon.skill = unit.tm
          pokemon.maxPP = 100
          pokemon.pp = 100
        }
        if ((unit.fireShardCount ?? 0) > 0) {
          pokemon.addAttack(3 * (unit.fireShardCount ?? 0))
          pokemon.addSpeed(3 * (unit.fireShardCount ?? 0))
        }
        if ((unit.smokedFiletCount ?? 0) > 0) {
          const smokedFiletCount = Math.min(unit.smokedFiletCount ?? 0, 10)
          pokemon.addMaxHP(-5 * smokedFiletCount)
          pokemon.addAttack(3 * smokedFiletCount)
          pokemon.addAbilityPower(10 * smokedFiletCount)
        }
        if ((unit.groundHoleAttackBonus ?? 0) > 0) {
          pokemon.addAttack(unit.groundHoleAttackBonus ?? 0)
        }
        if ((unit.groundHoleDefenseBonus ?? 0) > 0) {
          pokemon.addDefense(unit.groundHoleDefenseBonus ?? 0)
          pokemon.addSpecialDefense(unit.groundHoleDefenseBonus ?? 0)
        }
        if (unit.supercharged) {
          pokemon.supercharged = true
          pokemon.addSpeed(20)
          pokemon.addShield(30)
        }
        unit.dishes.forEach((dish) => pokemon.dishes.add(dish))

        const boardY = isRedTeam ? BOARD_HEIGHT - unit.y : unit.y - 1
        const [x, y] = transformEntityCoordinates(unit.x, boardY, false)
        const sprite = new PokemonSprite(
          this as any,
          x,
          y,
          pokemon,
          teamId,
          false,
          false
        )
        sprite.draggable = false
        sprite.setAlpha(0.96)
        if (unit.spotlight) {
          sprite.addLight()
        }
        this.previewGroup?.add(sprite)
      })
    }

    renderTeam(preview.blue, this.uid, false)
    renderTeam(preview.red, "simulator-red", true)
    this.fitBattlefieldToViewport()
  }

  private async renderPreviewState(preview: SimulatorBattlePreview) {
    const requestId = ++this.pokemonAssetRequestId
    await this.ensureAssetsForPreview(preview)
    if (requestId !== this.pokemonAssetRequestId) {
      return
    }
    if (this.pendingSimulation) {
      return
    }
    this.renderPreview(preview)
  }

  private async renderSimulationState(simulation: Simulation) {
    const requestId = ++this.pokemonAssetRequestId
    await this.ensureAssetsForSimulation(simulation)
    if (requestId !== this.pokemonAssetRequestId) {
      return
    }
    if (this.pendingSimulation !== simulation) {
      return
    }
    this.battle?.setSimulation(simulation)
    this.applyWeather(simulation.weather)
    this.updateSpotlightMarkers(simulation)
    this.updateGroundHoleMarkers(simulation)
    this.updateFlowerPotMarkers(simulation)
    if (simulation.started) {
      this.battle?.onSimulationStart()
    }
  }

  private async ensureAssetsForPreview(preview: SimulatorBattlePreview) {
    const pokemonConfigs = [
      ...preview.blue.map((unit) => ({ name: unit.name, shiny: unit.shiny })),
      ...preview.red.map((unit) => ({ name: unit.name, shiny: unit.shiny })),
      ...preview.blueFlowerPots.map((pot) => ({ name: pot.pokemon, shiny: false })),
      ...preview.redFlowerPots.map((pot) => ({ name: pot.pokemon, shiny: false }))
    ]
    await this.ensurePokemonAssets(pokemonConfigs)
  }

  private async ensureAssetsForSimulation(simulation: Simulation) {
    const pokemonConfigs = [
      ...Array.from(simulation.blueTeam.values()).map((pokemon) => ({
        index: pokemon.index,
        shiny: pokemon.shiny
      })),
      ...Array.from(simulation.redTeam.values()).map((pokemon) => ({
        index: pokemon.index,
        shiny: pokemon.shiny
      })),
      ...((this.pendingBluePlayer?.flowerPots ?? []).map((pokemon) => ({
        index: pokemon.index,
        shiny: pokemon.shiny
      })) as Array<{ index: string; shiny: boolean }>),
      ...((this.pendingRedPlayer?.flowerPots ?? []).map((pokemon) => ({
        index: pokemon.index,
        shiny: pokemon.shiny
      })) as Array<{ index: string; shiny: boolean }>)
    ]
    await this.ensurePokemonAssets(pokemonConfigs)
  }

  private async ensurePokemonAssets(
    pokemonConfigs: Array<
      | { name: Pkm; shiny: boolean }
      | { index: string; shiny: boolean }
    >
  ) {
    const tintRequests = new Map<string, Set<PokemonTint>>()

    pokemonConfigs.forEach((config) => {
      const index =
        "index" in config
          ? config.index
          : PokemonFactory.createPokemonFromName(config.name).index
      if (!index || index === "0000") {
        return
      }
      const tint = config.shiny ? PokemonTint.SHINY : PokemonTint.NORMAL
      const tints = tintRequests.get(index) ?? new Set<PokemonTint>()
      tints.add(tint)
      tintRequests.set(index, tints)
    })

    await Promise.all(
      Array.from(tintRequests.keys()).map(async (index) => {
        if (!this.textures.exists(index)) {
          await loadCompressedAtlas(this, index)
        }
      })
    )

    tintRequests.forEach((tints, index) => {
      tints.forEach((tint) => {
        this.animationManager?.createPokemonAnimations(index, tint)
      })
    })
  }

  private clearPreview() {
    this.previewGroup?.clear(true, true)
  }

  private emitSelectedPokemonDetail() {
    if (!this.onSelectedPokemonChange) {
      return
    }

    const selected = this.lastPokemonDetail
    if (!selected) {
      this.onSelectedPokemonChange(null)
      return
    }

    this.onSelectedPokemonChange({
      pokemon: selected.pokemon,
      origin: selected.inBattle ? "battle" : "team",
      isAlly: selected.playerId === this.uid,
      version: ++this.selectedPokemonVersion
    })
  }
}

